import { useCallback, useEffect, useMemo, useState } from 'react';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { FilterGroup, FilterOption } from '@/components/Filter/types';
import useDebounceState from '@/hooks/useDebounceState';
import usePagination from '@/hooks/usePagination';
import {
  getSkillFilterOption,
  SkillMenuItem,
} from '@/modules/corporate/modules/feedback/views/FeedbackWall/components/CreateFeedbackModal/components/RelatedSkillsOrMindsets/utils';
import { SkillDetailsFragment } from '@/modules/skill-matrix/graphql/fragment/generated/SkillDetails';
import { useAllSkillsListQuery } from '@/modules/skill-matrix/graphql/queries/generated/allSkills';
import { PeerFeedbackSkillRatingInputType, SkillTypeEnum } from '@/types/schema';

import { useUserPraiseSkillsQuery } from '../../graphql/queries/generated/usersPraiseSkills';

const usePraiseRelatedSkills = ({
  userIds,
  skillRatings,
  onChange,
}: {
  userIds?: string[];
  skillRatings: PeerFeedbackSkillRatingInputType[];
  onChange: (value: PeerFeedbackSkillRatingInputType[]) => void;
}) => {
  const [open, { toggle }] = useToggle(false);
  const [searchText, setSearchText, debouncedSearchText] = useDebounceState('');
  const [selectedSkillsState, setSelectedSkills] = useState<SkillMenuItem[]>();
  const selectedSkillIds = skillRatings.map((selectedSkill) => selectedSkill.skillId);

  const [{ data: skillData, fetching }] = useAllSkillsListQuery({
    variables: {
      filters: {
        ids: selectedSkillIds,
        types: [SkillTypeEnum.Ability],
      },
      first: -1,
    },
  });
  const selectedSkills = useMemo(
    () => skillData?.skills.edges?.map((skillNode) => skillNode.node),
    [skillData?.skills.edges],
  );

  const { data } = usePagination(
    useUserPraiseSkillsQuery,
    {
      filters: {
        ids: userIds,
      },
      first: -1,
    },
    { edgeKey: 'users', pause: !userIds?.length },
  );

  useEffect(() => {
    if (selectedSkillIds.length && !selectedSkillsState && !fetching) {
      const selectedSkillsMap = selectedSkills?.map((skill) => getSkillFilterOption(skill));
      setSelectedSkills(selectedSkillsMap);
    }
  }, [fetching, selectedSkillIds.length, selectedSkills, selectedSkillsState]);

  const filteredSkills = useMemo(() => {
    const allSkills = data.reduce((acc, node) => {
      if (node.orgRole?.skills?.length) {
        acc.push(...node.orgRole!.skills!);
      }
      return acc;
    }, [] as SkillDetailsFragment[]);
    const skills = uniqBy(allSkills, 'id');

    return debouncedSearchText
      ? skills.filter((skill) =>
          skill.name.toLowerCase().includes(debouncedSearchText.toLowerCase()),
        )
      : skills;
  }, [data, debouncedSearchText]);

  const filterGroups: FilterGroup[] = useMemo(() => {
    return filteredSkills?.length && userIds?.length
      ? [
          {
            id: 'skills',
            options: filteredSkills.map((skill) => getSkillFilterOption(skill)),
          },
        ]
      : [];
  }, [filteredSkills, userIds?.length]);

  const onAddSkill = useEvent((filterOptions: FilterOption[]) => {
    setSelectedSkills(filterOptions as SkillMenuItem[]);
    const keyBySkillRatings = keyBy(skillRatings, 'skillId');
    const newSkillRatings = (filterOptions as SkillMenuItem[]).map((skillOption) => ({
      skillId: skillOption.id,
      rating: keyBySkillRatings[skillOption.id]?.rating,
    }));
    onChange(newSkillRatings);
  });

  const onRemoveSkill = useCallback(
    (id: string) => {
      const updatedSkills = skillRatings.filter((skill) => skill.skillId !== id);
      const updatedSelectedSkillsState = selectedSkillsState?.filter(
        (skillMenuItem: SkillMenuItem) => skillMenuItem.id !== id,
      );
      setSelectedSkills(updatedSelectedSkillsState);
      onChange(updatedSkills);
    },
    [onChange, selectedSkillsState, skillRatings],
  );

  return {
    selectedSkills: selectedSkillIds.length ? selectedSkills : [],
    fetching,
    searchText,
    setSearchText,
    filterGroups,
    open,
    toggle,
    onAddSkill,
    selectedSkillsState,
    onRemoveSkill,
  };
};

export default usePraiseRelatedSkills;
